import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const Success = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const orderDetails = location.state?.orderDetails;

  useEffect(() => {
    if (!orderDetails) {
      // If no order details, redirect to shop after a short delay
      const timer = setTimeout(() => {
        navigate('/shop');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [orderDetails, navigate]);

  // Debug log
  console.log('Order Details:', JSON.stringify(orderDetails, null, 2));

  if (!orderDetails) {
    return (
      <div className="min-h-screen bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4">Kļūda</h1>
            <p className="text-gray-400">Nevarēja atrast pasūtījuma informāciju.</p>
            <p className="text-gray-400 mt-2">Tiek veikta pārvirzīšana uz veikalu...</p>
            <button
              onClick={() => navigate('/shop')}
              className="mt-6 bg-green-400 hover:bg-green-500 text-black font-semibold py-2 px-4 rounded-md transition-colors"
            >
              Atgriezties veikalā
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Safely access nested properties with type checking
  const billingDetails = orderDetails.billingDetails || {};
  const address = billingDetails.address || {};

  // Debug log
  console.log('Address:', address);
  console.log('Billing Details:', billingDetails);

  return (
    <div className="min-h-screen bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8 mt-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold mb-2">Paldies par jūsu pasūtījumu!</h1>
          <p className="text-gray-400">Jūsu pasūtījums ir veiksmīgi apstrādāts.</p>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Pasūtījuma informācija</h2>
          <div className="space-y-4">
            <div>
              <p className="text-gray-400">Pasūtījuma ID</p>
              <p className="font-medium">{orderDetails.id || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-400">Summa</p>
              <p className="font-medium">€{((orderDetails.amount || 0) / 100).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-gray-400">Statuss</p>
              <p className="font-medium capitalize">{orderDetails.status || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-400">Datums</p>
              <p className="font-medium">{orderDetails.created || 'N/A'}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Piegādes informācija</h2>
          <div className="space-y-4">
            <div>
              <p className="text-gray-400">Vārds, Uzvārds</p>
              <p className="font-medium">{billingDetails.name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-400">E-pasts</p>
              <p className="font-medium">{billingDetails.email || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-400">Telefons</p>
              <p className="font-medium">{billingDetails.phone || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-400">Adrese</p>
              <p className="font-medium">
                {address.line1 || 'N/A'}<br />
                {address.city && address.postal_code ? `${address.city}, ${address.postal_code}` : 'N/A'}<br />
                {address.country || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {orderDetails.items && Array.isArray(orderDetails.items) && (
          <div className="bg-gray-800 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Pasūtītās preces</h2>
            <div className="space-y-4">
              {orderDetails.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{item.name || 'N/A'}</p>
                    <p className="text-gray-400">Skaits: {item.quantity || 0}</p>
                  </div>
                  <p className="font-medium">€{(item.price || 0).toFixed(2)}</p>
                </div>
              ))}
              <div className="border-t border-gray-700 pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <p className="font-semibold">Kopā:</p>
                  <p className="font-semibold">€{(orderDetails.total || 0).toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="text-center">
          <button
            onClick={() => navigate('/shop')}
            className="bg-green-400 hover:bg-green-500 text-black font-semibold py-2 px-4 rounded-md transition-colors"
          >
            Atgriezties veikalā
          </button>
        </div>
      </div>
    </div>
  );
};

export default Success; 